import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button } from '@ds';
import {
  CommonRouteLegSchemaValues,
  LegFormValues,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  RouteLegFormMode,
  useLegFormContext,
} from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import {
  ValidationBanners,
  GroundTripValidationBanners,
} from '@lp/features/RoutePlanner/components/RouteLegForm/components/banners';
import { useWatchFields } from '@/hooks/useWatchFields';
import { RouteLegTypeEnum } from '@/api/apis/route';
import { retrieveUnifiedError } from '@/api/utils';
import { LegDuration } from './LegDuration';

type LegFormFooterProps = {
  onDelete?: (data: LegFormValues) => Promise<void>;
  onClose: () => void;
};

export const LegFormFooter = ({ onDelete, onClose }: LegFormFooterProps) => {
  const {
    getValues,
    formState: { isSubmitting },
  } = useFormContext<LegFormValues>();
  const { mode, isMainDataEdit, isRemoveAllowed, isOptionalDataEdit, errorMessages, isSubmitEnabled } =
    useLegFormContext();
  const { legType } = useWatchFields<CommonRouteLegSchemaValues>(['legType'] as const);

  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleteLoading(true);

      await onDelete?.(getValues());
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const isTmdxDriveAffiliateLeg = legType === RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg;
  const isTmdxDriveTedsLeg = legType === RouteLegTypeEnum.TmdxDriveTedsGroundLeg;
  const showButtons = isMainDataEdit || isOptionalDataEdit;
  const isEditMode = mode === RouteLegFormMode.Edit;
  const isDisabledByErrors = !!errorMessages.length;

  const showGroundTripValidation = isTmdxDriveAffiliateLeg || isTmdxDriveTedsLeg;

  const isSubmitButtonDisabled = !isSubmitEnabled || isDeleteLoading || isDisabledByErrors;

  return (
    <>
      {showGroundTripValidation && <GroundTripValidationBanners isEditMode={isEditMode} />}
      <ValidationBanners />
      <hr className="border-1 border-base-40 w-full" />
      <div className="w-full flex flex-row items-center justify-between">
        <LegDuration />
        {showButtons && (
          <div className="flex flex-row gap-4 ml-auto">
            {isRemoveAllowed && (
              <Button
                size="md"
                variant="light"
                color="warning"
                onPress={handleDelete}
                isLoading={isDeleteLoading}
                isDisabled={isSubmitting}
              >
                Delete
              </Button>
            )}
            <Button size="md" variant="outline" onPress={onClose} isDisabled={isSubmitting || isDeleteLoading}>
              {isEditMode ? 'Discard' : 'Cancel'}
            </Button>
            <Button
              size="md"
              type="submit"
              variant="solid"
              isLoading={isSubmitting}
              isDisabled={isSubmitButtonDisabled}
            >
              {isEditMode ? 'Save' : 'Add Leg'}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
